import initialState from 'archiving/db'
import initialMessagesState from 'archiving/messages/db'
import getIn from 'lodash/get'
import setIn from 'lodash/set'

import { createNextState } from '@reduxjs/toolkit'

import {
  ARCHIVING_MESSAGES_CLEAR_AUTHORIZATION_COUNT,
  ARCHIVING_MESSAGES_CLEAR_DOWNLOAD_URI_TEMPLATE,
  ARCHIVING_MESSAGES_CLEAR_NEXT_LINK,
  ARCHIVING_MESSAGES_CLEAR_PREVIEW,
  ARCHIVING_MESSAGES_CLEAR_RESULTS,
  ARCHIVING_MESSAGES_CLEAR_SCROLL_TO_INDEX,
  ARCHIVING_MESSAGES_CLEAR_SELECTION,
  ARCHIVING_MESSAGES_CLEAR_SID,
  ARCHIVING_MESSAGES_CLEAR_SOURCE,
  ARCHIVING_MESSAGES_CONCAT_RESULTS,
  ARCHIVING_MESSAGES_ENABLE_HIGHLIGHTING,
  ARCHIVING_MESSAGES_INIT_DB,
  ARCHIVING_MESSAGES_SELECT_ROWS,
  ARCHIVING_MESSAGES_SET_ARCHIVING_URI,
  ARCHIVING_MESSAGES_SET_AUTHORIZATION_COUNT,
  ARCHIVING_MESSAGES_SET_AUTHORIZATION_TIMESTAMP,
  ARCHIVING_MESSAGES_SET_COLUMN_WIDTH,
  ARCHIVING_MESSAGES_SET_DATE_RANGE,
  ARCHIVING_MESSAGES_SET_DATE_RANGE_HIDDEN,
  ARCHIVING_MESSAGES_SET_DATE_RANGE_SHOWN,
  ARCHIVING_MESSAGES_SET_DOWNLOAD_URI_TEMPLATE,
  ARCHIVING_MESSAGES_SET_NEXT_LINK,
  ARCHIVING_MESSAGES_SET_OFFSET,
  ARCHIVING_MESSAGES_SET_PREVIEW,
  ARCHIVING_MESSAGES_SET_QUERY,
  ARCHIVING_MESSAGES_SET_RESULTS,
  ARCHIVING_MESSAGES_SET_RESULTS_QUERY,
  ARCHIVING_MESSAGES_SET_SCROLL_TO_INDEX,
  ARCHIVING_MESSAGES_SET_SID,
  ARCHIVING_MESSAGES_SET_SOURCE,
  ARCHIVING_MESSAGES_SET_TIME_ZONE_KEY,
  ARCHIVING_MESSAGES_START_LOADING,
  ARCHIVING_MESSAGES_STOP_LOADING,
  ARCHIVING_MESSAGES_TOGGLE_ATTACHMENT_PANEL
} from './actions'

// todo instead of 'messages', it should be a link name from SSV-2936
const getBaseKeyPath = (companyKey) => ['companies', companyKey, 'messages']
const getKeyPath = (companyKey, subKey) => getBaseKeyPath(companyKey).concat([subKey])
const getResultsKeyPath = (companyKey) => getKeyPath(companyKey, 'results')

const reducers = {
  [ARCHIVING_MESSAGES_CLEAR_AUTHORIZATION_COUNT]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'authorizationCount'), -1)
  },

  [ARCHIVING_MESSAGES_CLEAR_DOWNLOAD_URI_TEMPLATE]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'downloadUriTemplate'), null)
  },

  [ARCHIVING_MESSAGES_CLEAR_NEXT_LINK]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'nextLink'), null)
  },

  [ARCHIVING_MESSAGES_CLEAR_PREVIEW]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'preview'), null)
  },

  [ARCHIVING_MESSAGES_CLEAR_SID]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'sid'), null)
  },

  [ARCHIVING_MESSAGES_CLEAR_RESULTS]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'results'), null)
  },

  [ARCHIVING_MESSAGES_CLEAR_SCROLL_TO_INDEX]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'scrollToIndex'), null)
  },

  [ARCHIVING_MESSAGES_CLEAR_SELECTION]: (archivingDb, { companyKey }) => {
    const results = getIn(archivingDb, getResultsKeyPath(companyKey))

    if (!results) return archivingDb

    return setIn(
      archivingDb,
      getResultsKeyPath(companyKey),
      results.map((result) => {
        delete result.selected
        return result
      })
    )
  },

  [ARCHIVING_MESSAGES_CLEAR_SOURCE]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'source'), null)
  },

  [ARCHIVING_MESSAGES_CONCAT_RESULTS]: (archivingDb, action) => {
    const results = getIn(archivingDb, getResultsKeyPath(action.companyKey))

    return setIn(
      archivingDb,
      getResultsKeyPath(action.companyKey),
      results.concat(action.moreResults)
    )
  },

  [ARCHIVING_MESSAGES_ENABLE_HIGHLIGHTING]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'highlight'), action.enabled)
  },

  [ARCHIVING_MESSAGES_INIT_DB]: (archivingDb, action) => {
    return setIn(archivingDb, getBaseKeyPath(action.companyKey), initialMessagesState)
  },

  [ARCHIVING_MESSAGES_SELECT_ROWS]: (archivingDb, action) => {
    const { companyKey, rows, selected } = action

    const results = getIn(archivingDb, getResultsKeyPath(companyKey))

    return setIn(
      archivingDb,
      getResultsKeyPath(action.companyKey),
      results.map((result) => {
        const found = rows.find((row) => row.sid === result.sid)

        if (!found) return result

        return Object.assign(result, { selected })
      })
    )
  },

  [ARCHIVING_MESSAGES_SET_ARCHIVING_URI]: (archivingDb, action) => {
    return setIn(
      archivingDb,
      getKeyPath(action.companyKey, 'archivingUri'),
      action.archivingUri
    )
  },

  [ARCHIVING_MESSAGES_SET_AUTHORIZATION_COUNT]: (archivingDb, action) => {
    return setIn(
      archivingDb,
      getKeyPath(action.companyKey, 'authorizationCount'),
      action.count
    )
  },

  [ARCHIVING_MESSAGES_SET_AUTHORIZATION_TIMESTAMP]: (archivingDb, action) => {
    return setIn(
      archivingDb,
      getKeyPath(action.companyKey, 'authorizationTimestamp'),
      action.timestamp
    )
  },

  [ARCHIVING_MESSAGES_SET_COLUMN_WIDTH]: (archivingDb, action) => {
    return setIn(
      archivingDb,
      getKeyPath(action.companyKey, 'columns').concat([action.columnKey, 'width']),
      action.width
    )
  },

  [ARCHIVING_MESSAGES_SET_DATE_RANGE]: (archivingDb, action) => {
    const messages = getIn(archivingDb, getBaseKeyPath(action.companyKey))

    messages.fromDate = action.fromDate
    messages.thruDate = action.thruDate

    return setIn(archivingDb, getBaseKeyPath(action.companyKey), messages)
  },

  [ARCHIVING_MESSAGES_SET_DATE_RANGE_SHOWN]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'dateRangeShown'), true)
  },

  [ARCHIVING_MESSAGES_SET_DATE_RANGE_HIDDEN]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'dateRangeShown'), false)
  },

  [ARCHIVING_MESSAGES_SET_DOWNLOAD_URI_TEMPLATE]: (archivingDb, action) => {
    return setIn(
      archivingDb,
      getKeyPath(action.companyKey, 'downloadUriTemplate'),
      action.downloadUriTemplate
    )
  },

  [ARCHIVING_MESSAGES_SET_SID]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'sid'), action.sid)
  },

  [ARCHIVING_MESSAGES_SET_NEXT_LINK]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'nextLink'), action.nextLink)
  },

  [ARCHIVING_MESSAGES_SET_OFFSET]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'offset'), action.offset)
  },

  [ARCHIVING_MESSAGES_SET_PREVIEW]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'preview'), action.preview)
  },

  [ARCHIVING_MESSAGES_SET_QUERY]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'query'), action.query)
  },

  [ARCHIVING_MESSAGES_SET_RESULTS]: (archivingDb, action) => {
    return setIn(archivingDb, getResultsKeyPath(action.companyKey), action.results)
  },

  [ARCHIVING_MESSAGES_SET_RESULTS_QUERY]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'resultsQuery'), action.query)
  },

  [ARCHIVING_MESSAGES_SET_SCROLL_TO_INDEX]: (archivingDb, action) => {
    return setIn(
      archivingDb,
      getKeyPath(action.companyKey, 'scrollToIndex'),
      action.scrollToIndex
    )
  },

  [ARCHIVING_MESSAGES_SET_SOURCE]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'source'), action.source)
  },

  [ARCHIVING_MESSAGES_SET_TIME_ZONE_KEY]: (archivingDb, action) => {
    return setIn(
      archivingDb,
      getKeyPath(action.companyKey, 'timeZoneName'),
      action.timeZoneName
    )
  },

  [ARCHIVING_MESSAGES_START_LOADING]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'loading'), true)
  },

  [ARCHIVING_MESSAGES_STOP_LOADING]: (archivingDb, action) => {
    return setIn(archivingDb, getKeyPath(action.companyKey, 'loading'), false)
  },

  [ARCHIVING_MESSAGES_TOGGLE_ATTACHMENT_PANEL]: (archivingDb, action) => {
    return setIn(
      archivingDb,
      getKeyPath(action.companyKey, 'attachmentPanelExpanded'),
      action.expanded
    )
  }
}

export default function (moduleDb = initialState, action = {}) {
  return createNextState(moduleDb, (draftState) => {
    const reducer = reducers[action.type]

    if (!reducer) return draftState

    return reducer(draftState, action)
  })
}
